import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Add, Clear } from '@mui/icons-material';
import { useProduct } from '../../context/ProductContext';
import { Product } from '../../models/Product';
import { useDebounce } from '../../hooks/useDebounce';

interface ProductGroupSearchProps {
  onProductSelect: (product: Product) => void;
}

const ProductGroupSearch: React.FC<ProductGroupSearchProps> = ({
  onProductSelect,
}) => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { getProducts } = useProduct();
  const debouncedQuery = useDebounce(query, 300);

  // Search for products when the debounced query changes
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedQuery.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const result = await getProducts({
          query: debouncedQuery,
          page: 1,
          limit: 10,
        });
        setSearchResults(result.products || []);
      } catch (err: any) {
        console.error('Error searching products:', err);
        setError('Nie można wyszukać produktów');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedQuery, getProducts]);

  // Handle click outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectProduct = (product: Product) => {
    onProductSelect(product);
    setQuery('');
    setSearchResults([]);
    inputRef.current?.focus();
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults([]);
    inputRef.current?.focus();
  };

  // Animation variants
  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scaleY: 0.9, transformOrigin: 'top' },
    visible: {
      opacity: 1,
      y: 0,
      scaleY: 1,
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      y: -10,
      scaleY: 0.9,
      transition: { duration: 0.2 },
    },
  };

  const resultItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  return (
    <div ref={searchContainerRef} className="relative">
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder="Szukaj produktów po nazwie lub kodzie..."
          className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
        />
        {query && (
          <button
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600"
            onClick={clearSearch}
          >
            <Clear fontSize="small" />
          </button>
        )}
      </div>

      {isSearching && (
        <div className="py-4 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      )}

      <AnimatePresence>
        {isFocused &&
          (query.trim() !== '' || searchResults.length > 0) &&
          !isSearching && (
            <motion.div
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg mt-1 z-10 max-h-72 overflow-y-auto"
            >
              {error ? (
                <div className="p-4 text-center text-red-500">
                  <p>{error}</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <p>Brak wyników wyszukiwania</p>
                </div>
              ) : (
                <ul className="py-1 divide-y divide-gray-100">
                  {searchResults.map((product) => (
                    <motion.li
                      key={product.id}
                      variants={resultItemVariants}
                      className="hover:bg-gray-50"
                    >
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2 flex items-center justify-between"
                        onClick={() => handleSelectProduct(product)}
                      >
                        <div className="flex items-center space-x-3">
                          {product.mobilePrimaryImage ? (
                            <img
                              src={product.mobilePrimaryImage}
                              alt={product.name}
                              className="h-10 w-10 object-contain bg-white rounded border border-gray-100"
                            />
                          ) : (
                            <div className="h-10 w-10 bg-gray-100 rounded-md flex items-center justify-center text-gray-400">
                              ?
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              {product.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              SKU: {product.serialNumber}
                            </p>
                          </div>
                        </div>
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="bg-primary text-white rounded-full p-1"
                        >
                          <Add fontSize="small" />
                        </motion.div>
                      </button>
                    </motion.li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
};

export default ProductGroupSearch;
